/**
 * TradeStack - Config Context
 *
 * Provides global configuration (tiers, categories, etc.)
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { configApi } from '../api';

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await configApi.getPublic();
        setConfig(response.data);

        // Apply theme colors to CSS variables
        if (response.data.branding) {
          applyThemeColors(response.data.branding);
        }
      } catch (err) {
        console.error('Failed to load config:', err);
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Apply theme colors to CSS variables
  const applyThemeColors = (branding) => {
    const root = document.documentElement;
    if (branding.primaryColor) {
      root.style.setProperty('--color-primary', branding.primaryColor);
    }
    if (branding.secondaryColor) {
      root.style.setProperty('--color-primary-dark', branding.secondaryColor);
    }
    if (branding.accentColor) {
      root.style.setProperty('--color-accent', branding.accentColor);
    }
  };

  // Helper functions
  const getTierById = (tierId) => {
    return config?.tiers?.find(t => t.id === tierId);
  };

  const getCategoryById = (categoryId) => {
    return config?.expenseCategories?.find(c => c.id === categoryId);
  };

  const getCategoryName = (categoryId) => {
    const category = getCategoryById(categoryId);
    return category?.name || categoryId;
  };

  const getJobStatusColor = (statusId) => {
    const status = config?.jobStatuses?.find(s => s.id === statusId);
    return status?.color || '#6B7280';
  };

  const getJobStatusName = (statusId) => {
    const status = config?.jobStatuses?.find(s => s.id === statusId);
    return status?.name || statusId;
  };

  const getCurrentMileageRate = () => {
    const year = new Date().getFullYear();
    return config?.mileageRates?.[year] || 0.67;
  };

  const isFeatureEnabled = (featureName, userTier = 'free') => {
    const tier = getTierById(userTier);
    return tier?.features?.[featureName] ?? false;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const value = {
    config,
    loading,
    error,
    // Helpers
    getTierById,
    getCategoryById,
    getCategoryName,
    getJobStatusColor,
    getJobStatusName,
    getCurrentMileageRate,
    isFeatureEnabled,
    formatCurrency,
    formatDate,
    // Direct access
    tiers: config?.tiers || [],
    expenseCategories: config?.expenseCategories || [],
    jobStatuses: config?.jobStatuses || [],
    contractorTypes: config?.contractorTypes || [],
    mileageRates: config?.mileageRates || {},
    platform: config?.platform || {}
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
